import type { Card, Language } from '../../../../shared/types';

/**
 * What each hands-free mode actually asks of the learner.
 *
 * The three modes differ only in which side is spoken to you and which side you
 * speak back, but getting that mapping backwards is the kind of bug that does
 * not crash — it just quietly drills the opposite skill. So the mapping is a
 * pure function with a test per mode rather than a branch inside the component.
 *
 * Echo is the one that needs no recognition at all: you hear the target and say
 * it back, which is shadowing practice, and nothing has to judge it. That makes
 * it the only mode that works with no microphone permission and no network, so
 * it is the fallback whenever the other two cannot run.
 */

export type HandsFreeMode = 'recall' | 'production' | 'echo';

export interface HandsFreeStep {
  /** What the app says to set up the card. */
  promptText: string;
  /** BCP 47 tag the prompt is spoken in. */
  promptLang: string;
  /** What the learner is meant to say back. */
  expectedText: string;
  /** BCP 47 tag to listen in. */
  listenLang: string;
  /** Whether an article slip should be surfaced — true only for the target language. */
  articlesMatter: boolean;
  /** Language code for article rules, e.g. "es" or "en". */
  matchLanguage: string;
  /** Spoken after the answer, to confirm the correct form. */
  revealText: string;
  /** Tag for the reveal. */
  revealLang: string;
  /** Echo is graded by the learner, not the matcher. */
  graded: boolean;
}

export const MODE_LABELS: Record<HandsFreeMode, { name: string; blurb: string }> = {
  recall: {
    name: 'Hear it, translate it',
    blurb: 'You hear the Spanish and say the English.',
  },
  production: {
    name: 'Hear English, say it',
    blurb: 'You hear the English and say the Spanish. The harder direction, and the useful one.',
  },
  echo: {
    name: 'Listen and repeat',
    blurb: 'You hear the Spanish and say it back. Needs no microphone — pure pronunciation practice.',
  },
};

/**
 * The language the translations are written in.
 *
 * The course is authored in English throughout, so this is a constant rather
 * than a setting. It becomes a parameter the day a Spanish-language edition
 * exists, and not before.
 */
export const NATIVE_LANG = 'en-US';

export function describeStep(mode: HandsFreeMode, card: Card, language: Language): HandsFreeStep {
  const targetLang = language.speechTag;
  const base = language.code;

  switch (mode) {
    case 'recall':
      return {
        promptText: card.target,
        promptLang: targetLang,
        expectedText: card.translation,
        listenLang: NATIVE_LANG,
        // Nobody is learning English gender from this app.
        articlesMatter: false,
        matchLanguage: 'en',
        revealText: card.translation,
        revealLang: NATIVE_LANG,
        graded: true,
      };

    case 'production':
      return {
        promptText: card.translation,
        promptLang: NATIVE_LANG,
        expectedText: card.target,
        listenLang: targetLang,
        // "el" versus "la" is the lesson here.
        articlesMatter: true,
        matchLanguage: base,
        revealText: card.target,
        revealLang: targetLang,
        graded: true,
      };

    case 'echo':
      return {
        promptText: card.target,
        promptLang: targetLang,
        expectedText: card.target,
        listenLang: targetLang,
        articlesMatter: true,
        matchLanguage: base,
        revealText: card.translation,
        revealLang: NATIVE_LANG,
        // There is nothing to mark: you either said it along with the voice or
        // you did not, and only you know that.
        graded: false,
      };
  }
}

/**
 * Words that steer the drill rather than answer the card.
 *
 * Kept deliberately short and distinct. A command list that overlaps real
 * vocabulary would eat answers — "repeat" is safe in an English answer slot,
 * but this is checked before matching, so anything added here is a word the
 * learner can no longer give as an answer. That is the whole cost of each one.
 */
export type VoiceCommand = 'repeat' | 'skip' | 'stop' | 'correct' | 'wrong';

const COMMANDS: Record<VoiceCommand, string[]> = {
  repeat: ['repeat', 'again', 'say again', 'say it again', 'one more time'],
  skip: ['skip', 'skip it', 'pass', 'next', 'next card'],
  stop: ['stop', 'stop drill', 'quit', 'pause', 'exit'],
  // Overrides, for when the matcher gets it wrong. Being able to say "I was
  // right" matters more than the matcher being perfect.
  correct: ['i was right', 'that was right', 'mark correct', 'override'],
  wrong: ['i was wrong', 'that was wrong', 'mark wrong'],
};

/** Returns the command in `heard`, or null if it is an ordinary answer. */
export function parseVoiceCommand(heard: string): VoiceCommand | null {
  const cleaned = heard
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return null;

  for (const [command, phrases] of Object.entries(COMMANDS) as [VoiceCommand, string[]][]) {
    // Exact only. A card whose answer happens to contain "next" must not be
    // swallowed as a command, so containment is not enough.
    if (phrases.includes(cleaned)) return command;
  }
  return null;
}
